import { motion, useMotionTemplate, useMotionValue } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import bgLeft from "@/assets/icons/bg-left.svg";
import bgLines from "@/assets/icons/bg-lines.svg";
import bgPixels from "@/assets/icons/bg-pixels.png";
import bgRight from "@/assets/icons/bg-right.svg";
import eYoshlarLogo from "@/assets/icons/e-yoshlar-logo.svg";
import { LanguageSelector } from "@/widgets/LanguageSelector";

const characters =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

const generateRandomString = (length: number) => {
  let result = "";
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

interface HoverPatternProps {
  mouseX: any;
  mouseY: any;
  randomString: string;
}

const HoverPattern = ({ mouseX, mouseY, randomString }: HoverPatternProps) => {
  const maskImage = useMotionTemplate`radial-gradient(200px at ${mouseX}px ${mouseY}px, white, transparent)`;
  const style = { maskImage, WebkitMaskImage: maskImage };

  return (
    <div className="pointer-events-none absolute inset-0 z-[12]">
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-green-500 to-blue-700 opacity-0 backdrop-blur-xl transition duration-500 group-hover/bg:opacity-100"
        style={style}
      />
      <motion.div
        className="absolute inset-0 opacity-0 mix-blend-overlay group-hover/bg:opacity-100"
        style={style}
      >
        <motion.div
          className="absolute overflow-hidden"
          style={{
            left: mouseX,
            top: mouseY,
            translateX: "-50%",
            translateY: "-50%",
            width: "400px",
            height: "400px",
          }}
        >
          <p className="font-mono w-full break-all text-xs font-bold leading-[1.2] text-white">
            {randomString}
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const AuthLayout = () => {
  const { t } = useTranslation("login");
  const privacyPolicyUrl = import.meta.env.VITE_PRIVACY_POLICY_URL || "#";

  // Hover effect state
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [randomString, setRandomString] = useState("");

  useEffect(() => {
    const str = generateRandomString(1500);
    setRandomString(str);
  }, []);

  const onMouseMove = ({
    currentTarget,
    clientX,
    clientY,
  }: React.MouseEvent<HTMLDivElement>) => {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);

    const str = generateRandomString(1500);
    setRandomString(str);
  };

  return (
    <div
      onMouseMove={onMouseMove}
      className="group/bg relative flex h-dvh w-full overflow-hidden p-5"
      style={{
        backgroundColor: "#E9F3FF",
      }}
    >
      <HoverPattern
        mouseX={mouseX}
        mouseY={mouseY}
        randomString={randomString}
      />
      <img
        src={bgLeft}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 z-[2] h-auto object-contain opacity-90"
        style={{ width: "min(72vw, 1172px)" }}
      />
      <img
        src={bgRight}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 z-[2] h-auto object-contain opacity-90"
        style={{ width: "min(72vw, 1172px)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[7] h-[60vh] min-h-[420px]"
        style={{
          backgroundImage: `url(${bgPixels})`,
          backgroundPosition: "center bottom",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          opacity: 0.9,
        }}
      />
      <img
        src={bgLines}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 z-[6] h-auto w-full object-contain object-bottom opacity-85"
      />
      <img
        src={eYoshlarLogo}
        alt="E Yoshlar"
        width={110}
        height={29}
        className="absolute left-5 top-5 z-30 object-contain"
      />
      <div className="absolute bottom-5 left-5 z-30 flex items-center gap-4">
        <LanguageSelector />
        <a
          href={privacyPolicyUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm underline-offset-4 transition-colors hover:text-text-primary hover:underline"
        >
          {t("privacyPolicy")}
        </a>
      </div>
      <div className="relative z-20 flex w-full flex-col">
        <main className="flex h-full w-full flex-col items-center justify-center">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
